import { useCallback, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { invokeAdmin } from "@/pages/backstage/lib/adminApi";

export type BausteinBooking = {
  id: string;
  booking_no: string | null;
  type: string | null;
  blocks: string | null;
  excursions: string | null;
  experiences: string | null;
  transfers: string | null;
  arrival_flight: string | null;
  departure_flight: string | null;
  arrival_pickup: string | null;
  departure_pickup: string | null;
  arrival_driver: string | null;
  departure_driver: string | null;
  transfer_total: number | null;
  total_price: number | null;
  subtotal: number | null;
  discount: number | null;
  tour_start: string | null;
  tour_end: string | null;
  adults: number | null;
  children: number | null;
  firstname: string | null;
  lastname: string | null;
  email: string | null;
  phone: string | null;
  nationality: string | null;
  message: string | null;
  lang: string | null;
  created_at: string;
};

type Props = {
  pin: string | null;
  isDe: boolean;
};

type PeriodKey = "all" | "today" | "next7" | "next30" | "next90";

type DraftFields = {
  arrival_pickup: string;
  departure_pickup: string;
  arrival_driver: string;
  departure_driver: string;
};

const PERIOD_OPTIONS: { key: PeriodKey; de: string; en: string }[] = [
  { key: "all", de: "Alle Zeiträume", en: "All periods" },
  { key: "today", de: "Heute", en: "Today" },
  { key: "next7", de: "Nächste 7 Tage", en: "Next 7 days" },
  { key: "next30", de: "Nächste 30 Tage", en: "Next 30 days" },
  { key: "next90", de: "Nächste 90 Tage", en: "Next 90 days" },
];

const formatDay = (iso: string | null, isDe: boolean) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(isDe ? "de-DE" : "en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatMoney = (value: number | null, isDe: boolean) => {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return `€ ${new Intl.NumberFormat(isDe ? "de-DE" : "en-GB", {
    maximumFractionDigits: 0,
  }).format(value)}`;
};

const splitTransfers = (raw: string | null): string[] => {
  if (!raw) return [];
  return raw
    .split(/\s*;\s*/)
    .map((s) => s.trim())
    .filter(Boolean);
};

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const todayISO = (): string => new Date().toISOString().slice(0, 10);

const addDaysISO = (n: number): string => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const periodRangeEnd = (key: PeriodKey): string | null => {
  switch (key) {
    case "today":
      return todayISO();
    case "next7":
      return addDaysISO(7);
    case "next30":
      return addDaysISO(30);
    case "next90":
      return addDaysISO(90);
    default:
      return null;
  }
};

const inPeriod = (b: BausteinBooking, key: PeriodKey): boolean => {
  if (key === "all") return true;
  const rangeStart = todayISO();
  const rangeEnd = periodRangeEnd(key);
  if (!rangeEnd) return true;
  const start = (b.tour_start || b.tour_end || "").slice(0, 10);
  const end = (b.tour_end || b.tour_start || "").slice(0, 10);
  if (!start || !end) return false;
  return start <= rangeEnd && end >= rangeStart;
};

const hasTransferData = (b: BausteinBooking): boolean =>
  splitTransfers(b.transfers).length > 0 ||
  Boolean(b.arrival_flight || b.departure_flight) ||
  Boolean(b.arrival_pickup || b.departure_pickup);

const draftFor = (b: BausteinBooking): DraftFields => ({
  arrival_pickup: b.arrival_pickup ?? "",
  departure_pickup: b.departure_pickup ?? "",
  arrival_driver: b.arrival_driver ?? "",
  departure_driver: b.departure_driver ?? "",
});

export default function BausteinBookings({ pin, isDe }: Props) {
  const [bookings, setBookings] = useState<BausteinBooking[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [period, setPeriod] = useState<PeriodKey>("all");
  const [drafts, setDrafts] = useState<Record<string, DraftFields>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [deleting, setDeleting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadBookings = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data, error: err } = await invokeAdmin<{ bookings: BausteinBooking[] }>({
        action: "list_baustein_bookings",
        pin,
      });
      if (err) throw new Error(err);
      const list = data?.bookings ?? [];
      setBookings(list);
      const map: Record<string, DraftFields> = {};
      list.forEach((b) => {
        map[b.id] = draftFor(b);
      });
      setDrafts(map);
      setSelected({});
    } catch (e) {
      setError(
        isDe
          ? `Buchungen konnten nicht geladen werden: ${e instanceof Error ? e.message : "Fehler"}`
          : `Could not load bookings: ${e instanceof Error ? e.message : "error"}`
      );
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [pin, isDe]);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  // Nur Buchungen mit tatsächlichem Transfer/Flug/Abholort - das ist der Inhalt
  // der ausdruckbaren Transfer- & Flugübersicht.
  const transferOrFlightBookings = useMemo(
    () => bookings.filter(hasTransferData),
    [bookings]
  );

  // Dieselbe Liste, zusätzlich nach dem gewählten Zeitraum gefiltert.
  const overviewBookings = useMemo(
    () => transferOrFlightBookings.filter((b) => inPeriod(b, period)),
    [transferOrFlightBookings, period]
  );

  const updateDraft = (b: BausteinBooking, field: keyof DraftFields, value: string) => {
    setDrafts((prev) => {
      const cur = prev[b.id] ?? draftFor(b);
      return { ...prev, [b.id]: { ...cur, [field]: value } };
    });
  };

  const saveDraft = async (b: BausteinBooking) => {
    const d = drafts[b.id] ?? draftFor(b);
    setSavingId(b.id);
    setSavedId(null);
    setError("");
    try {
      const { data, error: err } = await invokeAdmin<{ ok: boolean; booking: BausteinBooking }>({
        action: "update_baustein_booking",
        pin,
        id: b.id,
        arrival_pickup: d.arrival_pickup,
        departure_pickup: d.departure_pickup,
        arrival_driver: d.arrival_driver,
        departure_driver: d.departure_driver,
      });
      if (err) throw new Error(err);
      if (data?.booking) {
        const updated = data.booking;
        setBookings((prev) => prev.map((x) => (x.id === b.id ? { ...x, ...updated } : x)));
        setDrafts((prev) => ({ ...prev, [b.id]: draftFor({ ...b, ...updated }) }));
      }
      setSavedId(b.id);
    } catch (e) {
      setError(
        isDe
          ? `Speichern fehlgeschlagen: ${e instanceof Error ? e.message : "Fehler"}`
          : `Saving failed: ${e instanceof Error ? e.message : "error"}`
      );
    } finally {
      setSavingId(null);
    }
  };

  const toggleSelected = (id: string) => {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const selectedIds = useMemo(
    () => bookings.filter((b) => selected[b.id]).map((b) => b.id),
    [bookings, selected]
  );
  const allSelected = bookings.length > 0 && selectedIds.length === bookings.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected({});
    } else {
      const next: Record<string, boolean> = {};
      bookings.forEach((b) => {
        next[b.id] = true;
      });
      setSelected(next);
    }
  };

  const deleteOne = async (b: BausteinBooking) => {
    const name = `${b.firstname || ""} ${b.lastname || ""}`.trim();
    const label = name || b.booking_no || "—";
    if (
      !window.confirm(
        isDe
          ? `Buchung von „${label}" endgültig löschen? Das kann nicht rückgängig gemacht werden.`
          : `Permanently delete the booking for "${label}"? This cannot be undone.`
      )
    )
      return;
    setDeletingId(b.id);
    setError("");
    setNotice("");
    try {
      const { error: err } = await invokeAdmin<{ ok?: boolean }>({
        action: "delete_baustein_booking",
        pin,
        id: b.id,
      });
      if (err) throw new Error(err);
      setBookings((prev) => prev.filter((x) => x.id !== b.id));
      setSelected((prev) => {
        const next = { ...prev };
        delete next[b.id];
        return next;
      });
      setNotice(isDe ? "Buchung gelöscht." : "Booking deleted.");
    } catch (e) {
      setError(
        isDe
          ? `Löschen fehlgeschlagen: ${e instanceof Error ? e.message : "Fehler"}`
          : `Deletion failed: ${e instanceof Error ? e.message : "error"}`
      );
    } finally {
      setDeletingId(null);
    }
  };

  const deleteSelected = async () => {
    const ids = selectedIds;
    if (ids.length === 0) return;
    if (
      !window.confirm(
        isDe
          ? `${ids.length} Buchung(en) endgültig löschen? Das kann nicht rückgängig gemacht werden.`
          : `Permanently delete ${ids.length} booking(s)? This cannot be undone.`
      )
    )
      return;
    setDeleting(true);
    setError("");
    setNotice("");
    try {
      const { error: err } = await invokeAdmin<{ ok?: boolean }>({
        action: "delete_baustein_bookings",
        pin,
        ids,
      });
      if (err) throw new Error(err);
      const idSet = new Set(ids);
      setBookings((prev) => prev.filter((x) => !idSet.has(x.id)));
      setSelected({});
      setNotice(
        isDe
          ? `${ids.length} Buchung(en) gelöscht.`
          : `${ids.length} booking(s) deleted.`
      );
    } catch (e) {
      setError(
        isDe
          ? `Löschen fehlgeschlagen: ${e instanceof Error ? e.message : "Fehler"}`
          : `Deletion failed: ${e instanceof Error ? e.message : "error"}`
      );
    } finally {
      setDeleting(false);
    }
  };

  const handlePrintTransfers = () => {
    setNotice("");
    const dateStr = new Date().toLocaleDateString(isDe ? "de-DE" : "en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
    const periodLabel = PERIOD_OPTIONS.find((p) => p.key === period);
    const periodText = periodLabel ? (isDe ? periodLabel.de : periodLabel.en) : "";
    const docTitle = isDe
      ? "Villa Kompiang — Transfer- & Flugübersicht"
      : "Villa Kompiang — Transfer & flight overview";
    const heading = isDe ? "Transfer- & Flugübersicht" : "Transfer & flight overview";
    const brand = "Villa Kompiang";
    const standLine = isDe ? `Stand: ${dateStr}` : `As of ${dateStr}`;
    const countLine = isDe
      ? `${overviewBookings.length} Buchung(en) mit Transfer/Flug · Zeitraum: ${periodText}`
      : `${overviewBookings.length} booking(s) with transfer/flight · Period: ${periodText}`;
    const cols = isDe
      ? ["Buchungs-Nr.", "Gast", "Reisezeitraum", "Transfers", "Ankunft", "Abreise", "Fahrer"]
      : ["Booking no.", "Guest", "Travel period", "Transfers", "Arrival", "Departure", "Driver"];
    const footNote = isDe
      ? "Diese Übersicht wird automatisch aus dem Backstage erzeugt und dient als Fahrer- und Planungsblatt für Transfers und Flüge."
      : "This overview is generated automatically from the backstage and serves as a driver and planning sheet for transfers and flights.";

    const headHtml = cols.map((c) => `<th>${escapeHtml(c)}</th>`).join("");

    const rows = overviewBookings
      .map((b) => {
        const transfers = splitTransfers(b.transfers);
        const transferCell =
          transfers.length > 0 ? transfers.map((t) => escapeHtml(t)).join("<br />") : "—";

        const arrivalParts: string[] = [];
        if (b.arrival_flight) arrivalParts.push(escapeHtml(b.arrival_flight));
        if (b.arrival_pickup)
          arrivalParts.push(
            `<span class="pickup">${isDe ? "Abholort" : "Pickup"}: ${escapeHtml(b.arrival_pickup)}</span>`
          );
        const arrivalCell = arrivalParts.length > 0 ? arrivalParts.join("<br />") : "—";

        const departureParts: string[] = [];
        if (b.departure_flight) departureParts.push(escapeHtml(b.departure_flight));
        if (b.departure_pickup)
          departureParts.push(
            `<span class="pickup">${isDe ? "Abreise zum" : "Departure to"}: ${escapeHtml(b.departure_pickup)}</span>`
          );
        const departureCell = departureParts.length > 0 ? departureParts.join("<br />") : "—";

        const driverParts: string[] = [];
        if (b.arrival_driver)
          driverParts.push(`${isDe ? "Ankunft" : "Arrival"}: ${escapeHtml(b.arrival_driver)}`);
        if (b.departure_driver)
          driverParts.push(`${isDe ? "Abreise" : "Departure"}: ${escapeHtml(b.departure_driver)}`);
        const driverCell = driverParts.length > 0 ? driverParts.join("<br />") : "—";

        const guest = escapeHtml(`${b.firstname || ""} ${b.lastname || ""}`.trim());
        return `<tr>
          <td class="mono">${escapeHtml(b.booking_no || "—")}</td>
          <td>${guest}${
            b.blocks ? `<br /><span class="sub">${escapeHtml(b.blocks)}</span>` : ""
          }</td>
          <td class="nowrap">${escapeHtml(formatDay(b.tour_start, isDe))} – ${escapeHtml(formatDay(b.tour_end, isDe))}</td>
          <td>${transferCell}</td>
          <td class="flight">${arrivalCell}</td>
          <td class="flight">${departureCell}</td>
          <td class="driver">${driverCell}</td>
        </tr>`;
      })
      .join("");

    const html = `<!DOCTYPE html><html lang="${isDe ? "de" : "en"}"><head><meta charset="utf-8" /><title>${escapeHtml(docTitle)}</title><style>
      @page { size: A4 landscape; margin: 14mm 12mm; }
      * { box-sizing: border-box; }
      body { margin: 0; font-family: Georgia, 'Times New Roman', serif; color: #2b2622; background: #fff; font-size: 11px; line-height: 1.45; }
      header { border-bottom: 2px solid #b08968; padding-bottom: 12px; margin-bottom: 18px; }
      .brand { font-size: 10px; letter-spacing: .18em; text-transform: uppercase; color: #8a6d52; margin: 0 0 4px; }
      h1 { font-size: 22px; margin: 0 0 6px; color: #2b2622; }
      .meta { font-size: 11px; color: #6b6259; margin: 0; }
      table { width: 100%; border-collapse: collapse; }
      th, td { text-align: left; padding: 6px 8px; border-bottom: 1px solid #e8e1d9; vertical-align: top; }
      th { font-size: 9.5px; letter-spacing: .06em; text-transform: uppercase; color: #8a6d52; border-bottom: 1.5px solid #d9cfc3; }
      .mono { font-family: 'Courier New', monospace; font-size: 10px; white-space: nowrap; }
      .nowrap { white-space: nowrap; }
      .sub { color: #8a6d52; font-size: 10px; }
      .pickup { color: #4a5568; font-style: italic; }
      .flight { color: #6b4f34; }
      .driver { color: #3d5a4a; }
      tbody tr:nth-child(even) { background: #faf7f3; }
      footer { margin-top: 20px; padding-top: 10px; border-top: 1px solid #e8e1d9; font-size: 10px; color: #6b6259; }
    </style></head><body>
      <header>
        <p class="brand">${escapeHtml(brand)}</p>
        <h1>${escapeHtml(heading)}</h1>
        <p class="meta">${escapeHtml(standLine)} · ${escapeHtml(countLine)}</p>
      </header>
      <table><thead><tr>${headHtml}</tr></thead><tbody>${rows}</tbody></table>
      <footer>${escapeHtml(footNote)}</footer>
    </body></html>`;

    const printWindow = window.open("", "_blank", "width=1200,height=900");
    if (!printWindow) {
      setNotice(
        isDe
          ? "Bitte erlaube Pop-ups für diese Seite, um die PDF-Ansicht zu öffnen."
          : "Please allow pop-ups for this page to open the PDF view."
      );
      return;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 350);
  };

  const handleExportExcel = () => {
    setNotice("");
    const periodLabel = PERIOD_OPTIONS.find((p) => p.key === period);
    const periodText = periodLabel ? (isDe ? periodLabel.de : periodLabel.en) : "";
    const dateStr = new Date().toLocaleDateString(isDe ? "de-DE" : "en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });

    const headers = isDe
      ? [
          "Buchungs-Nr.",
          "Gast",
          "Reisezeitraum",
          "Transfers",
          "Flug Ankunft",
          "Abholort Ankunft",
          "Fahrer Ankunft",
          "Flug Abreise",
          "Abreise zum",
          "Fahrer Abreise",
        ]
      : [
          "Booking no.",
          "Guest",
          "Travel period",
          "Transfers",
          "Arrival flight",
          "Arrival pickup",
          "Arrival driver",
          "Departure flight",
          "Departure to",
          "Departure driver",
        ];

    const title = isDe
      ? "Villa Kompiang — Transfer- & Flugübersicht"
      : "Villa Kompiang — Transfer & flight overview";
    const metaLine = isDe
      ? `Stand: ${dateStr} · ${overviewBookings.length} Buchung(en) · Zeitraum: ${periodText}`
      : `As of ${dateStr} · ${overviewBookings.length} booking(s) · Period: ${periodText}`;

    const rows: string[][] = overviewBookings.map((b) => {
      const guest = `${b.firstname || ""} ${b.lastname || ""}`.trim();
      const periodCell = `${formatDay(b.tour_start, isDe)} - ${formatDay(b.tour_end, isDe)}`;
      return [
        b.booking_no || "",
        guest,
        periodCell,
        splitTransfers(b.transfers).join("\n"),
        b.arrival_flight || "",
        b.arrival_pickup || "",
        b.arrival_driver || "",
        b.departure_flight || "",
        b.departure_pickup || "",
        b.departure_driver || "",
      ];
    });

    const aoa: string[][] = [[title], [metaLine], [], headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(aoa);

    worksheet["!cols"] = [
      { wch: 16 },
      { wch: 24 },
      { wch: 24 },
      { wch: 34 },
      { wch: 24 },
      { wch: 24 },
      { wch: 18 },
      { wch: 24 },
      { wch: 24 },
      { wch: 18 },
    ];
    worksheet["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: headers.length - 1 } },
    ];
    worksheet["!autofilter"] = {
      ref: XLSX.utils.encode_range({
        s: { r: 3, c: 0 },
        e: { r: aoa.length - 1, c: headers.length - 1 },
      }),
    };

    const workbook = XLSX.utils.book_new();
    const sheetName = isDe ? "Transfers & Flüge" : "Transfers & flights";
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    XLSX.writeFile(workbook, `transfer-fluguebersicht-${todayISO()}.xlsx`);
    setNotice(
      isDe
        ? "Excel-Datei wurde erstellt und heruntergeladen."
        : "Excel file created and downloaded."
    );
  };

  const periodSelectValue = period;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-heading text-2xl text-foreground-950">
            {isDe ? "Bausteine- & Transfer-Buchungen" : "Modules & transfer bookings"}
          </h2>
          <p className="text-sm text-foreground-600 mt-1">
            {isDe
              ? "Alle Anfragen aus dem Baustein-Planer inklusive Transfers, Flugdaten, Abholort und Fahrer."
              : "All requests from the module planner including transfers, flight details, pickup and driver."}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 shrink-0">
          <button
            onClick={handlePrintTransfers}
            disabled={loading || overviewBookings.length === 0}
            className="inline-flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-60 text-background-50 text-sm font-medium px-5 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
          >
            <i className="ri-file-pdf-2-line"></i>
            {isDe ? "Transfer- & Flugübersicht drucken" : "Print transfer & flight overview"}
          </button>
          <button
            onClick={handleExportExcel}
            disabled={loading || overviewBookings.length === 0}
            className="inline-flex items-center justify-center gap-2 bg-accent-600 hover:bg-accent-700 disabled:opacity-60 text-background-50 text-sm font-medium px-5 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
          >
            <i className="ri-file-excel-2-line"></i>
            {isDe ? "Als Excel exportieren" : "Export as Excel"}
          </button>
          <button
            onClick={loadBookings}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 bg-background-100 hover:bg-background-200 disabled:opacity-60 text-foreground-800 border border-background-300 text-sm font-medium px-5 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
          >
            <i className={loading ? "ri-loader-4-line animate-spin" : "ri-refresh-line"}></i>
            {isDe ? "Aktualisieren" : "Refresh"}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6 bg-background-100 border border-background-200 rounded-md px-4 py-3">
        <label
          htmlFor="transfer_period"
          className="text-sm font-medium text-foreground-800 whitespace-nowrap"
        >
          <i className="ri-calendar-2-line text-accent-600 mr-1.5"></i>
          {isDe ? "Zeitraum (Druck & Export)" : "Period (print & export)"}
        </label>
        <select
          id="transfer_period"
          value={periodSelectValue}
          onChange={(e) => setPeriod(e.target.value as PeriodKey)}
          className="px-3 py-2 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
        >
          {PERIOD_OPTIONS.map((p) => (
            <option key={p.key} value={p.key}>
              {isDe ? p.de : p.en}
            </option>
          ))}
        </select>
        <span className="text-sm text-foreground-600">
          {isDe
            ? `${overviewBookings.length} von ${transferOrFlightBookings.length} Buchung(en) mit Transfer/Flug`
            : `${overviewBookings.length} of ${transferOrFlightBookings.length} booking(s) with transfer/flight`}
        </span>
      </div>

      {notice && (
        <div className="mb-6 text-sm font-medium text-primary-700 bg-primary-100 border border-primary-200 rounded-md px-4 py-3">
          {notice}
        </div>
      )}

      {error && (
        <div className="mb-6 text-sm font-medium text-secondary-700 bg-secondary-100 border border-secondary-200 rounded-md px-4 py-3">
          {error}
        </div>
      )}

      {!loading && bookings.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-4 bg-background-50 border border-background-200 rounded-md px-4 py-3">
          <label className="inline-flex items-center gap-2 text-sm font-medium text-foreground-800 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleSelectAll}
              className="w-4 h-4 cursor-pointer accent-primary-600"
            />
            {isDe ? "Alle auswählen" : "Select all"}
          </label>
          <span className="text-sm text-foreground-600">
            {isDe ? `${selectedIds.length} ausgewählt` : `${selectedIds.length} selected`}
          </span>
          <button
            type="button"
            onClick={deleteSelected}
            disabled={selectedIds.length === 0 || deleting}
            className="ml-auto inline-flex items-center gap-2 bg-secondary-600 hover:bg-secondary-700 disabled:opacity-60 text-background-50 text-sm font-medium px-4 py-2 rounded-md whitespace-nowrap cursor-pointer transition-colors"
          >
            <i className={deleting ? "ri-loader-4-line animate-spin" : "ri-delete-bin-line"}></i>
            {isDe ? "Ausgewählte löschen" : "Delete selected"}
          </button>
        </div>
      )}

      {loading && bookings.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-foreground-600">
          <i className="ri-loader-4-line animate-spin"></i>
          {isDe ? "Wird geladen..." : "Loading..."}
        </div>
      ) : bookings.length === 0 ? (
        <div className="bg-background-100 border border-background-200 rounded-lg p-10 text-center text-foreground-600">
          {isDe ? "Noch keine Baustein-Buchungen vorhanden." : "No module bookings yet."}
        </div>
      ) : (
        <div className="space-y-4">
          {bookings.map((b) => {
            const transfers = splitTransfers(b.transfers);
            const hasFlights = Boolean(b.arrival_flight || b.departure_flight);
            const hasData = hasTransferData(b);
            const guests = (b.adults || 0) + (b.children || 0);
            const draft = drafts[b.id] ?? draftFor(b);
            const isSaving = savingId === b.id;
            const isSaved = savedId === b.id;
            return (
              <div
                key={b.id}
                className="bg-background-50 border border-background-200 rounded-lg p-5"
              >
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <input
                        type="checkbox"
                        checked={!!selected[b.id]}
                        onChange={() => toggleSelected(b.id)}
                        className="w-4 h-4 shrink-0 cursor-pointer accent-primary-600"
                        aria-label={isDe ? "Buchung auswählen" : "Select booking"}
                      />
                      <span className="font-heading text-lg text-foreground-950">
                        {b.firstname} {(b.lastname || "").toUpperCase()}
                      </span>
                      {b.blocks && (
                        <span className="text-xs bg-accent-100 text-accent-800 px-2 py-0.5 rounded-full whitespace-nowrap">
                          {b.blocks}
                        </span>
                      )}
                      {b.booking_no && (
                        <span className="text-xs bg-accent-100 text-accent-800 px-2 py-0.5 rounded-full whitespace-nowrap font-mono">
                          {b.booking_no}
                        </span>
                      )}
                      {b.lang && (
                        <span className="text-xs bg-background-100 text-foreground-600 px-2 py-0.5 rounded-full whitespace-nowrap uppercase">
                          {b.lang}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-foreground-700">
                      <span>
                        <i className="ri-calendar-line text-accent-600 mr-1"></i>
                        {formatDay(b.tour_start, isDe)} — {formatDay(b.tour_end, isDe)}
                      </span>
                      <span>
                        <i className="ri-user-line text-accent-600 mr-1"></i>
                        {isDe
                          ? `${b.adults || 0} Erwachsene, ${b.children || 0} Kinder (${guests} Gäste)`
                          : `${b.adults || 0} adults, ${b.children || 0} children (${guests} guests)`}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-foreground-700">
                      {b.email && (
                        <span>
                          <i className="ri-mail-line text-accent-600 mr-1"></i>
                          {b.email}
                        </span>
                      )}
                      {b.phone && (
                        <span>
                          <i className="ri-phone-line text-accent-600 mr-1"></i>
                          {b.phone}
                        </span>
                      )}
                      {b.nationality && (
                        <span>
                          <i className="ri-global-line text-accent-600 mr-1"></i>
                          {b.nationality}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs uppercase tracking-wide text-foreground-500">
                      {isDe ? "Gesamtpreis" : "Total"}
                    </div>
                    <div className="font-heading text-lg text-foreground-950">
                      {formatMoney(b.total_price, isDe)}
                    </div>
                    {b.discount ? (
                      <div className="text-xs text-secondary-700">
                        {isDe ? `inkl. Rabatt ${formatMoney(b.discount, isDe)}` : `incl. discount ${formatMoney(b.discount, isDe)}`}
                      </div>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => deleteOne(b)}
                      disabled={deletingId === b.id}
                      className="mt-2 inline-flex items-center gap-1.5 text-xs text-secondary-700 bg-secondary-100 hover:bg-secondary-200 border border-secondary-200 px-3 py-1.5 rounded-md whitespace-nowrap cursor-pointer transition-colors disabled:opacity-60"
                    >
                      <i className={deletingId === b.id ? "ri-loader-4-line animate-spin" : "ri-delete-bin-line"}></i>
                      {isDe ? "Löschen" : "Delete"}
                    </button>
                  </div>
                </div>

                {hasData && (
                  <>
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                      {transfers.length > 0 && (
                        <div className="bg-background-100 rounded-md px-4 py-3">
                          <div className="text-xs font-medium uppercase tracking-wide text-foreground-500 mb-1.5 flex items-center gap-1.5">
                            <i className="ri-roadster-line text-accent-600"></i>
                            {isDe ? "Transfers" : "Transfers"}
                          </div>
                          <ul className="space-y-1 text-sm text-foreground-800">
                            {transfers.map((line, i) => (
                              <li key={i}>{line}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {hasFlights && (
                        <div className="bg-primary-100 border border-primary-200 rounded-md px-4 py-3">
                          <div className="text-xs font-medium uppercase tracking-wide text-primary-700 mb-1.5 flex items-center gap-1.5">
                            <i className="ri-flight-takeoff-line text-primary-700"></i>
                            {isDe ? "Flugdaten" : "Flight details"}
                          </div>
                          <ul className="space-y-1 text-sm text-foreground-900">
                            {b.arrival_flight && (
                              <li>
                                <span className="text-foreground-600 mr-1.5">
                                  {isDe ? "Ankunft:" : "Arrival:"}
                                </span>
                                {b.arrival_flight}
                              </li>
                            )}
                            {b.departure_flight && (
                              <li>
                                <span className="text-foreground-600 mr-1.5">
                                  {isDe ? "Abreise:" : "Departure:"}
                                </span>
                                {b.departure_flight}
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>

                    <div className="mt-3 bg-background-100 border border-background-200 rounded-md px-4 py-3">
                      <div className="text-xs font-medium uppercase tracking-wide text-foreground-500 mb-2 flex items-center gap-1.5">
                        <i className="ri-steering-2-line text-accent-600"></i>
                        {isDe ? "Fahrer & Abholort" : "Driver & pickup"}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-foreground-600 mb-1">
                            {isDe ? "Abholort Ankunft" : "Pickup arrival"}
                          </label>
                          <input
                            type="text"
                            value={draft.arrival_pickup}
                            onChange={(e) => updateDraft(b, "arrival_pickup", e.target.value)}
                            placeholder={isDe ? "z. B. Hotel / Adresse" : "e.g. hotel / address"}
                            className="w-full px-3 py-2 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-foreground-600 mb-1">
                            {isDe ? "Fahrer Ankunft" : "Driver arrival"}
                          </label>
                          <input
                            type="text"
                            value={draft.arrival_driver}
                            onChange={(e) => updateDraft(b, "arrival_driver", e.target.value)}
                            placeholder={isDe ? "Name des Fahrers" : "Driver name"}
                            className="w-full px-3 py-2 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-foreground-600 mb-1">
                            {isDe ? "Abreise zum" : "Departure to"}
                          </label>
                          <input
                            type="text"
                            value={draft.departure_pickup}
                            onChange={(e) => updateDraft(b, "departure_pickup", e.target.value)}
                            placeholder={isDe ? "z. B. Hotel / Adresse" : "e.g. hotel / address"}
                            className="w-full px-3 py-2 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-foreground-600 mb-1">
                            {isDe ? "Fahrer Abreise" : "Driver departure"}
                          </label>
                          <input
                            type="text"
                            value={draft.departure_driver}
                            onChange={(e) => updateDraft(b, "departure_driver", e.target.value)}
                            placeholder={isDe ? "Name des Fahrers" : "Driver name"}
                            className="w-full px-3 py-2 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                          />
                        </div>
                      </div>
                      <div className="mt-3 flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => saveDraft(b)}
                          disabled={isSaving}
                          className="inline-flex items-center gap-2 bg-accent-600 hover:bg-accent-700 disabled:opacity-60 text-background-50 text-sm font-medium px-4 py-2 rounded-md whitespace-nowrap cursor-pointer transition-colors"
                        >
                          <i className={isSaving ? "ri-loader-4-line animate-spin" : "ri-save-line"}></i>
                          {isSaving
                            ? isDe
                              ? "Speichern..."
                              : "Saving..."
                            : isDe
                            ? "Speichern"
                            : "Save"}
                        </button>
                        {isSaved && (
                          <span className="inline-flex items-center gap-1.5 text-xs text-accent-700">
                            <i className="ri-check-line"></i>
                            {isDe ? "Gespeichert" : "Saved"}
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {b.excursions && (
                  <div className="mt-3 text-sm text-foreground-700">
                    <i className="ri-map-pin-line text-accent-600 mr-1"></i>
                    <span className="text-foreground-600 mr-1.5">
                      {isDe ? "Bausteine:" : "Modules:"}
                    </span>
                    {b.excursions}
                  </div>
                )}

                {b.message && (
                  <p className="mt-3 text-sm text-foreground-600 bg-background-100 rounded-md px-3 py-2 whitespace-pre-line">
                    {b.message}
                  </p>
                )}

                <div className="mt-2 text-xs text-foreground-500">
                  {isDe ? "Eingegangen am" : "Received on"}{" "}
                  {new Date(b.created_at).toLocaleString(isDe ? "de-DE" : "en-GB")}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}